/**
 * CommunityMangaGen — inline CTA shown on a drill detail panel when no
 * step-by-step manga is available. Any athlete can spend one of their
 * daily quota slots to generate panels for the whole community.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Sparkles, Loader2, AlertCircle, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityMangaGen = ({ drillName, hasStaticSteps, onGenerated }) => {
  const [quota, setQuota] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [justGenerated, setJustGenerated] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchQuota = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/community/manga/quota`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQuota(res.data);
      } catch {
        /* silent — feature still usable, just no quota display */
      }
    };
    fetchQuota();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/community/manga/generate/${encodeURIComponent(drillName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 240000 }
      );
      setJustGenerated(true);
      setQuota((q) => (q ? { ...q, remaining: res.data.remaining_today, used: q.used + 1 } : q));
      onGenerated?.(res.data.panels);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't generate. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  if (!hasStaticSteps) return null; // can't generate without static seed
  if (justGenerated) {
    return (
      <div
        className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-sm"
        data-testid="community-manga-generated"
      >
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <span className="text-xs text-emerald-400 font-bold uppercase tracking-wide">
          Manga generated · refresh to view
        </span>
      </div>
    );
  }

  const noQuota = quota && quota.remaining <= 0;

  return (
    <div
      className="p-4 bg-gradient-to-br from-orange-500/10 to-zinc-900/20 border border-orange-500/30 rounded-sm space-y-3"
      data-testid="community-manga-cta"
    >
      <div className="flex items-start gap-2">
        <Users className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-orange-400 mb-1">
            Community Manga · Be the first
          </p>
          <p className="text-xs text-zinc-300 leading-relaxed">
            No step-by-step manga exists for this drill yet. Generate one with AI — it'll save for every athlete on the app.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-1.5 text-[10px] text-red-400" data-testid="community-manga-error">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={handleGenerate}
          disabled={generating || noQuota}
          className="flex-1 min-w-[180px] bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white text-xs font-black uppercase tracking-wider py-2.5 px-4 flex items-center justify-center gap-2 transition-colors"
          data-testid="community-manga-generate-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Drawing… (~30s)
            </>
          ) : noQuota ? (
            <>Daily Limit Reached</>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5" />
              Generate Manga
            </>
          )}
        </button>
        {quota && (
          <span
            className="text-[10px] font-mono text-zinc-400 whitespace-nowrap"
            data-testid="community-manga-quota"
          >
            {quota.remaining}/{quota.limit} left today
          </span>
        )}
      </div>
    </div>
  );
};

export default CommunityMangaGen;
